import React from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import { Container, Row } from 'reactstrap'
import {
  BootstrapTextInput,
  SaveButton,
  TypeaheadMultiInput,
  TypeaheadSingleInput,
  TypeaheadTagsInput,
  FormCard,
} from '../../components/forms'
import { _MOALPRESS_CONFIG } from '../../utils/empties'
import {
  useAllQuery,
  useSingleQuery,
} from '../../utils/hooks/reactQuery/queries'
import { useItemMutation } from '../../utils/hooks/reactQuery/mutations'

const MpConfigSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  tags: Yup.array().of(Yup.string()),
  baseUrl: Yup.string().required('Base URL is required'),
  authorId: Yup.number().required('Author is required'),
  categoryIds: Yup.array()
    .of(Yup.number())
    .min(1, 'Must provide at least one category')
    .required('Categories is required'),
  tagIds: Yup.array().of(Yup.number()),
  portfolioIds: Yup.array().of(Yup.number()),
  productId: Yup.number(),
  accessLevelId: Yup.number(),
})

function MoalPressConfig() {
  const { id } = useParams()
  const {
    data = _MOALPRESS_CONFIG,
    isLoading,
    isError,
    error,
  } = useSingleQuery('moalpress-config', id)
  const { add, update } = useItemMutation('moalpress-config')
  const { data: authors = {} } = useAllQuery('mp-authors')
  const { data: categories = {} } = useAllQuery('mp-categories')
  const { data: tags = {} } = useAllQuery('mp-tags')
  const { data: products = {} } = useAllQuery('mp-products')
  const { data: portfolios = {} } = useAllQuery('mp-portfolios')
  const { data: accessLevels = {} } = useAllQuery('mp-access-levels')

  const renderSiteSpecifics = (values) => {
    if (values.baseUrl.length > 0) {
      return (
        <>
          <Row>
            <TypeaheadSingleInput
              fullWidth
              label="Product"
              id="productId"
              name="productId"
              placeholder="Product"
              options={
                products.hasOwnProperty(values.baseUrl)
                  ? products[values.baseUrl]
                  : []
              }
              objects
              labelKey="name"
              valueKey="id"
              renderMenuItemChildren={(option) => (
                <div>
                  {option.name}
                  <div>
                    <small>Item Code: {option.item_number}</small>
                  </div>
                </div>
              )}
            />
            <TypeaheadSingleInput
              label="Access Level"
              id="accessLevelId"
              name="accessLevelId"
              placeholder="Access Level"
              options={
                accessLevels.hasOwnProperty(values.baseUrl)
                  ? accessLevels[values.baseUrl]
                  : []
              }
              objects
              labelKey="name"
              valueKey="id"
            />
            <TypeaheadSingleInput
              label="Author"
              id="authorId"
              name="authorId"
              placeholder="Author"
              options={
                authors.hasOwnProperty(values.baseUrl)
                  ? authors[values.baseUrl]
                  : []
              }
              objects
              labelKey="name"
              valueKey="id"
            />
            <TypeaheadMultiInput
              label="Categories"
              id="categoryIds"
              name="categoryIds"
              placeholder="Categories"
              options={
                categories.hasOwnProperty(values.baseUrl)
                  ? categories[values.baseUrl]
                  : []
              }
              objects
              labelKey="name"
              valueKey="id"
              renderMenuItemChildren={(option) => (
                <div>
                  {option.name}
                  <div>
                    <small>Slug: {option.slug}</small>
                  </div>
                </div>
              )}
            />
            <TypeaheadMultiInput
              fullWidth
              label="Tags"
              id="tagIds"
              name="tagIds"
              placeholder="Tags"
              options={
                tags.hasOwnProperty(values.baseUrl) ? tags[values.baseUrl] : []
              }
              objects
              labelKey="name"
              valueKey="id"
            />
            <TypeaheadMultiInput
              label="Portfolios"
              id="portfolioIds"
              name="portfolioIds"
              placeholder="Portfolios"
              options={
                portfolios.hasOwnProperty(values.baseUrl)
                  ? portfolios[values.baseUrl]
                  : []
              }
              objects
              labelKey="name"
              valueKey="id"
            />
          </Row>
        </>
      )
    } else {
      return <div />
    }
  }

  const handleSubmit = (values, { setSubmitting }) => {
    const toastId = toast.loading('Saving web config...')

    // Are we updating or adding?
    if (id !== 'new') {
      update.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved web config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save web config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    } else {
      add.mutate(values, {
        onSuccess: () => {
          toast.update(toastId, {
            render: 'Saved web config!',
            type: 'success',
            isLoading: false,
            autoClose: 1500,
          })
        },
        onError: (error) => {
          console.error(error)
          toast.update(toastId, {
            render: 'Could not save web config...',
            type: 'error',
            isLoading: false,
            autoClose: 1500,
          })
          setSubmitting(false)
        },
      })
    }
  }

  return (
    <Container fluid>
      {isLoading ? (
        <h5>Loading...</h5>
      ) : isError ? (
        <h5>Error: {error.message}</h5>
      ) : (
        <FormCard
          breadcrumbLink="/dashboard/moalpress-configs"
          breadcrumbLabel="MoalPress Configs"
          breadcrumbItem={
            data.name.length > 0 ? data.name : 'New MoalPress Config'
          }
        >
          <Formik
            initialValues={data}
            validationSchema={MpConfigSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isValid, isSubmitting, values }) => (
              <Form>
                <Row>
                  <BootstrapTextInput
                    label="Name"
                    id="name"
                    name="name"
                    placeholder="Name"
                  />
                  <TypeaheadTagsInput />
                  <TypeaheadSingleInput
                    fullWidth
                    label="Base URL"
                    id="baseUrl"
                    name="baseUrl"
                    placeholder="Base URL"
                    options={['dev.mp.monumenttradersalliance.com']}
                  />
                </Row>
                {renderSiteSpecifics(values)}
                <SaveButton disabled={isSubmitting || !isValid} />
              </Form>
            )}
          </Formik>
        </FormCard>
      )}
    </Container>
  )
}

export default MoalPressConfig
